import { supabase } from '@/integrations/supabase/client';

export interface MediaUploadResult {
  success: boolean;
  fileUrl?: string;
  fileName?: string;
  fileSize?: number;
  mimeType?: string;
  error?: string;
}

export interface MediaFile {
  id: string;
  file: File;
  preview: string;
  type: 'image' | 'video' | 'audio' | 'document';
  status: 'uploading' | 'ready' | 'error';
  progress?: number;
  mediaUrl?: string;
}

class MediaUploadService {
  private static instance: MediaUploadService;
  
  static getInstance(): MediaUploadService {
    if (!MediaUploadService.instance) {
      MediaUploadService.instance = new MediaUploadService();
    }
    return MediaUploadService.instance;
  }

  async uploadMedia(file: File, tenantId: string): Promise<MediaUploadResult> {
    try {
      console.log(`[MediaUpload] Starting upload for ${file.name} (${file.type}, ${file.size} bytes) to tenant ${tenantId}`);
      
      // Validate file type and size
      if (!this.validateFileType(file)) {
        console.log(`[MediaUpload] File type validation failed for ${file.type}`);
        return { success: false, error: 'File type not supported' };
      }

      if (!this.validateFileSize(file)) {
        console.log(`[MediaUpload] File size validation failed for ${file.size} bytes`);
        return { success: false, error: 'File size exceeds 50MB limit' };
      }

      // Get current user
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        console.log(`[MediaUpload] Authentication failed:`, authError);
        return { success: false, error: 'User not authenticated' };
      }

      // Generate unique filename under custom-storage
      const fileExtension = file.name.split('.').pop();
      const relativeName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExtension}`;
      const fileName = `${tenantId}/custom-storage/${relativeName}`;
      console.log(`[MediaUpload] Generated filename: ${fileName}`);

      // Upload to tenants bucket
      console.log(`[MediaUpload] Uploading to storage bucket: tenants`);
      const { data, error: uploadError } = await supabase.storage
        .from('tenants')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('[MediaUpload] Storage upload error:', uploadError);
        return { success: false, error: 'Failed to upload file' };
      }

      console.log(`[MediaUpload] Storage upload successful:`, data);

      // Generate signed URL for immediate use
      const { data: signedData } = await supabase.storage
        .from('tenants')
        .createSignedUrl(fileName, 3600);
      const publicUrl = signedData?.signedUrl || '';
      console.log(`[MediaUpload] Generated signed URL: ${publicUrl ? 'OK' : 'FAILED'}`);

      // Save media upload record
      console.log(`[MediaUpload] Saving media record to database`);
      const { data: mediaRecord, error: dbError } = await supabase
        .from('media_uploads')
        .insert({
          tenant_id: tenantId,
          user_id: user.id,
          file_path: fileName,
          original_filename: file.name,
          mime_type: file.type,
          file_size: file.size,
          upload_status: 'uploaded'
        })
        .select()
        .single();

      if (dbError) {
        console.error('[MediaUpload] Database error saving media record:', dbError);
        return { success: false, error: 'Failed to save media record' };
      }

      console.log(`[MediaUpload] Upload completed successfully for ${file.name}`);
      return {
        success: true,
        fileUrl: publicUrl,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type
      };
    } catch (error) {
      console.error('[MediaUpload] Upload error:', error);
      return { success: false, error: 'Upload failed' };
    }
  }

  private getMessageTypeFromFile(file: File): string {
    if (file.type.startsWith('image/')) return 'image';
    if (file.type.startsWith('video/')) return 'video';
    if (file.type.startsWith('audio/')) return 'audio';
    return 'document';
  }

  createMediaFile(file: File): MediaFile {
    const type = this.getMessageTypeFromFile(file) as 'image' | 'video' | 'audio' | 'document';
    
    return {
      id: Math.random().toString(36).substring(2),
      file,
      preview: type === 'image' ? URL.createObjectURL(file) : '',
      type,
      status: 'ready',
      progress: 0
    };
  }

  validateFileType(file: File): boolean {
    const allowedTypes = [
      'image/jpeg', 'image/png', 'image/gif', 'image/webp',
      'video/mp4', 'video/mpeg', 'video/quicktime',
      'audio/mpeg', 'audio/ogg', 'audio/wav', 'audio/mp4',
      'application/pdf', 'application/msword', 
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain', 'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    
    console.log(`[MediaUpload] Validating file type: ${file.type}`);
    return allowedTypes.includes(file.type);
  }

  validateFileSize(file: File): boolean {
    // 50MB limit
    const maxSize = 50 * 1024 * 1024;
    console.log(`[MediaUpload] Validating file size: ${file.size} bytes (max: ${maxSize})`);
    return file.size <= maxSize;
  }
}

export const mediaUploadService = MediaUploadService.getInstance();